// Parsers for the admin Teams page: bulk team import (paste / CSV / XLSX) and the roster's bulk
// player box. Pure functions (moved out of Teams.js unchanged in behaviour).

// Does a token look like a Steam input (link / Steam64 / Steam32 / STEAM_x:y:z / [U:1:..])?
// Used to find where the (possibly space-containing) team name ends and the player list begins —
// so "Team Saturn <link> <link>" parses WITHOUT quotes. Quotes still work as an explicit override.
export function looksLikeSteamInput(tok) {
  if (!tok) return false;
  const s = tok.trim();
  return (
    /steamcommunity\.com/i.test(s) ||        // profile / vanity URL
    /^\d{8,19}$/.test(s) ||                   // Steam32 account id or Steam64
    /^STEAM_[0-5]:[01]:\d+$/i.test(s) ||      // STEAM_x:y:z
    /^\[U:1:\d+\]$/i.test(s)                  // [U:1:accountid]
  );
}

// "steaminput" or "steaminput|Player Name" → { steam, name }, else null.
export function parseSteamToken(tok) {
  const pipe = tok.indexOf('|');
  if (pipe !== -1) {
    const steam = tok.slice(0, pipe);
    if (looksLikeSteamInput(steam)) return { steam, name: tok.slice(pipe + 1) };
  }
  if (looksLikeSteamInput(tok)) return { steam: tok, name: '' };
  return null;
}

/**
 * Bulk team import text: one team per line. The team name is every token before the first Steam
 * token (or a leading "quoted name"). A steam token starts a player; "steam|Name" or the following
 * non-steam tokens fill that player's name.
 * @returns {{ line, name, players: {steam, name}[] }[]}
 */
export function parseBulkText(text) {
  const lines = (text || '').split('\n');
  const parsed = [];
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw || !raw.trim()) continue;
    let name = '';
    let tokens = [];
    const quoted = raw.match(/^\s*"([^"]+)"\s*(.*)$/);
    if (quoted) {
      name = quoted[1].trim();
      tokens = (quoted[2].match(/\S+/g) || []).filter(Boolean);
    } else {
      const all = raw.match(/\S+/g) || [];
      let firstSteamIdx = all.findIndex(tok => parseSteamToken(tok) !== null);
      if (firstSteamIdx === -1) firstSteamIdx = all.length;
      name = all.slice(0, firstSteamIdx).join(' ').trim();
      tokens = all.slice(firstSteamIdx).filter(Boolean);
    }
    const players = [];
    for (const tok of tokens) {
      const sp = parseSteamToken(tok);
      if (sp) players.push({ steam: sp.steam, name: (sp.name || '').trim() });
      else if (players.length) {
        const cur = players[players.length - 1];
        cur.name = (cur.name ? cur.name + ' ' + tok : tok).trim();
      }
    }
    if (!name && players.length === 0) continue;
    parsed.push({ line: i + 1, name, players });
  }
  return parsed;
}

/**
 * Roster bulk box: one player per line — "steamid - name", "steamid,name", "steamid | name",
 * "steamid<TAB>name" or just "steamid". A bare "-" is NOT a separator (vanity URLs contain hyphens).
 * @returns {{ line, steam, name }[]}
 */
export function parseBulkRoster(text) {
  const out = [];
  (text || '').split('\n').forEach((rawLine, i) => {
    const line = rawLine.trim();
    if (!line) return;
    const m = line.match(/^(.*?)(?:\t|\s*\|\s*|\s*,\s*|\s+-\s+)(.*)$/);
    let steam = line;
    let name = '';
    if (m) {
      steam = m[1].trim();
      name = (m[2] || '').trim();
      if (!steam) { steam = line; name = ''; }
    }
    out.push({ line: i + 1, raw: rawLine, steam, name });
  });
  return out;
}

/**
 * Read an uploaded CSV / XLSX / XLS into bulk-import text lines. Column A = team name; then a
 * steam cell opens a player and the next non-steam cell is its name. A header row is skipped.
 * Refuses files that aren't spreadsheets (magic bytes) and Steam IDs Excel already truncated.
 * @returns {Promise<{ ok: true, lines: string[] } | { ok: false, code: string, cells?: string }>}
 */
export async function readTeamSheet(file) {
  const nameLower = (file.name || '').toLowerCase();
  if (!['.csv', '.xlsx', '.xls'].some(ext => nameLower.endsWith(ext))) return { ok: false, code: 'wrongFileType' };
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  // .xlsx = ZIP (PK..); .xls = OLE2 (D0 CF 11 E0); .csv = plain text
  const isZip = bytes[0] === 0x50 && bytes[1] === 0x4B && (bytes[2] === 0x03 || bytes[2] === 0x05 || bytes[2] === 0x07);
  const isOle = bytes[0] === 0xD0 && bytes[1] === 0xCF && bytes[2] === 0x11 && bytes[3] === 0xE0;
  const isCsv = nameLower.endsWith('.csv');
  const looksBinary = (
    (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) || // PNG
    (bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF) ||                        // JPEG
    (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) ||                        // GIF
    (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) ||   // PDF
    (bytes[0] === 0x1F && bytes[1] === 0x8B)                                                // GZIP
  );
  if (isCsv) {
    if (looksBinary || bytes.subarray(0, 1024).includes(0x00)) return { ok: false, code: 'notPlainText' };
  } else if (looksBinary || (!isZip && !isOle)) {
    return { ok: false, code: 'wrongFileType' };
  }

  const XLSX = await import('xlsx');
  // raw:true keeps CSV cells as text — otherwise "76561198034202275" becomes a JS number that
  // can't hold 17 digits (…270, a wrong Steam64).
  const wb = XLSX.read(buf, { type: 'array', raw: isCsv });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  if (!sheet) return { ok: false, code: 'fileParseFailed' };
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false, defval: '' });

  // A Steam64 in a Number-formatted cell is already cut to 15 digits inside the file (or saved as
  // "7.65612E+16" in CSV) — unrecoverable, so refuse instead of importing wrong IDs.
  const lossy = [];
  rows.forEach((row, r) => (row || []).forEach((c, ci) => {
    if (ci === 0) return;
    if ((typeof c === 'number' && !Number.isSafeInteger(c)) || (typeof c === 'string' && /^\d(\.\d+)?e\+\d+$/i.test(c.trim()))) {
      lossy.push(XLSX.utils.encode_cell({ r, c: ci }));
    }
  }));
  if (lossy.length) return { ok: false, code: 'numericSteamIds', cells: lossy.slice(0, 8).join(', ') + (lossy.length > 8 ? '…' : '') };

  const lines = [];
  for (let r = 0; r < rows.length; r++) {
    const row = rows[r] || [];
    const name = String(row[0] == null ? '' : row[0]).trim();
    if (r === 0 && /^(team\s*name|team|name)$/i.test(name)) continue; // header row
    if (!name) continue;
    const tokens = [];
    let pending = null;
    for (const cell of row.slice(1).map(c => String(c == null ? '' : c).trim())) {
      if (!cell) continue;
      if (looksLikeSteamInput(cell)) { if (pending) tokens.push(pending); pending = cell; }
      else if (pending) { tokens.push(pending + '|' + cell); pending = null; }
    }
    if (pending) tokens.push(pending);
    lines.push(`"${name.replace(/"/g, '')}" ${tokens.join(' ')}`.trim());
  }
  if (!lines.length) return { ok: false, code: 'emptyFile' };
  return { ok: true, lines };
}

// Steam64 → AccountID (the number in CS2 backup files / [U:1:x]).
export function steam64ToAccountId(steam64) {
  try {
    // eslint-disable-next-line no-undef
    return (BigInt(steam64) - BigInt('76561197960265728')).toString();
  } catch (_) {
    return null;
  }
}
